import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { IProductCategory, Product } from '../sp-types/productType';
import { db } from './firebase';
import { StatusType } from '../sp-types/statusType';
import { updateItem } from './genericFB';

const PRODUCT_CATEGORY_COLLECTION_NAME = 'product-category';
const PRODUCT_COLLECTION_NAME = 'product';
const DB = db;

export async function createProductCategoryInFb(data: IProductCategory) {
  try {
    const docRef = doc(collection(DB, PRODUCT_CATEGORY_COLLECTION_NAME), data.name);
    await setDoc(docRef, data);
    console.log('Document written with ID: ', docRef.id);
  } catch (err) {
    console.log('error found!', err);
  }
}

export async function getAllCategories(): Promise<IProductCategory[] | null> {
  const materialsRef = collection(DB, PRODUCT_CATEGORY_COLLECTION_NAME); // Ensure FUTURE_ORDER_LOCATION is a string
  try {
    const querySnapshot = await getDocs(materialsRef);

    if (!querySnapshot.empty) {
      const categories = querySnapshot.docs.map((doc) => doc.data() as IProductCategory);
      return categories;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting documents:', error);
    return null;
  }
}

export const createProduct = async (
  accountId: string,
  product: Product,
): Promise<{ success: boolean; id?: string }> => {
  try {
    const { uuid, ...productWithoutUuid } = product;
    const productWithAccountId: Product = { ...productWithoutUuid, accountId };
    const productDocRef = uuid ? doc(DB, PRODUCT_COLLECTION_NAME, uuid) : doc(collection(DB, PRODUCT_COLLECTION_NAME));
    await setDoc(productDocRef, productWithAccountId, { merge: true });
    console.log('Product created with ID: ', productDocRef.id);
    return { success: true, id: productDocRef.id };
  } catch (error) {
    console.error('Error creating product:', error);
    return { success: false };
  }
};

export const deleteProduct = async (productDbId: string): Promise<void> => {
  try {
    const productDocRef = doc(DB, PRODUCT_COLLECTION_NAME, productDbId);
    await deleteDoc(productDocRef);
    console.log('Product deleted with ID: ', productDbId);
  } catch (error) {
    console.error('Error deleting product:', error);
  }
};

export const updateProduct = async (productDbId: string, updatedData: Partial<Product>): Promise<Product | null> => {
  return updateItem<Product>(productDbId, updatedData, PRODUCT_COLLECTION_NAME);
};
export const getAllProductsByAccountId = async (
  accountId: string,
  statusTypes?: StatusType[],
): Promise<Product[] | null> => {
  try {
    const productsCollectionRef = collection(DB, PRODUCT_COLLECTION_NAME);
    const q = query(productsCollectionRef, where('accountId', '==', accountId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log('No products found for this account.');
      return [];
    }

    const products: Product[] = querySnapshot.docs.map((doc) => {
      const productData = doc.data() as Product;
      productData.uuid = doc.id;
      return productData;
    });

    if (statusTypes?.length) {
      return products.filter((product) => statusTypes.includes(product.status));
    }
    return products;
  } catch (error) {
    console.error('Error getting products:', error);
    return null;
  }
};

export const getProductById = async (accountId: string, productId: string): Promise<Product | undefined> => {
  try {
    const productsCollectionRef = collection(DB, PRODUCT_COLLECTION_NAME);
    const q = query(productsCollectionRef, where('accountId', '==', accountId), where('id', '==', productId));

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // Assuming that productId is unique within the collection, return the first match
      const productDoc = querySnapshot.docs[0];
      return productDoc.data() as Product;
    } else {
      console.log('No such product!');
      return undefined;
    }
  } catch (error) {
    console.error('Error getting product:', error);
    return undefined;
  }
};

export const getProductByFirestoreId = async (firestoreId: string): Promise<Product | undefined> => {
  try {
    // Reference the document by its Firestore ID
    const productDocRef = doc(DB, PRODUCT_COLLECTION_NAME, firestoreId);

    // Get the document
    const productDoc = await getDoc(productDocRef);

    if (productDoc.exists()) {
      // Get the product data and include the Firestore ID as the uuid
      const productData = productDoc.data() as Product;
      productData.uuid = productDoc.id;
      return productData;
    } else {
      console.log('No such product!');
      return undefined;
    }
  } catch (error) {
    console.error('Error getting product by Firestore ID:', error);
    return undefined;
  }
};
